import { NumberInput } from "@impero-com/common/components/form/fields/numberInput";
import { TimePickerInput } from "@impero-com/common/components/form/fields/timePickerInput/timePickerInput";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    TimezoneKey,
    getAbbreviatedTimezoneName,
} from "@impero-com/common/utils/timezone";
import { Tooltip } from "@impero-com/ui";
import { Typography } from "antd";
import moment from "moment-timezone";

import {
    AllocatedTimeResponsible,
    MAX_NOTIFICATION_OFFSET_DAY_COUNT,
} from "../formData";

import { InputWithRightText } from "./InputWithRightText";
import * as theme from "./styles.css";

const { Text } = Typography;

interface AllocatedTimePickerResponsibleProps {
    value: AllocatedTimeResponsible;
    onChange: (value: AllocatedTimeResponsible) => void;
    disabled?: boolean;
    timezoneKey: TimezoneKey;
    calendarName?: string;
    sendImmediately: boolean;
}

export function AllocatedTimePickerResponsible({
    timezoneKey,
    calendarName,
    value: allocatedTime,
    onChange,
    disabled,
    sendImmediately,
}: AllocatedTimePickerResponsibleProps) {
    const { t } = useTranslation();

    const unitLabel =
        calendarName ??
        t("Controls_Schedule_Day", { count: allocatedTime.value });

    const transTargetLabel = t("CreateControl_Notification_ResponsibleHas");
    const taskLabel = t(`CreateControl_Notification_Task_Control`);
    const toPerformLabel = t("CreateControl_Notification_AssigneeToPerform", {
        taskLabel,
    });
    const pickerLabel = t("CreateControl_Notification_SendTo");

    const tooltipText = t(
        "CreateControl_ResponsiblePickerDisabled_TooltipText"
    );

    const tzAbbr = getAbbreviatedTimezoneName(timezoneKey);

    const handleValueChange = (newValue: number | null) => {
        onChange({ ...allocatedTime, value: newValue ?? 0 });
    };

    const handleSendTimeChange = (newSendTime: Date | null) => {
        if (newSendTime) {
            onChange({ ...allocatedTime, sendTime: moment(newSendTime) });
        }
    };

    const renderNumberInput = () => {
        if (sendImmediately) {
            return (
                <Tooltip title={tooltipText}>
                    <div>
                        <NumberInput
                            value={null}
                            disabled={true}
                            placeholder={undefined}
                            min={0}
                            max={MAX_NOTIFICATION_OFFSET_DAY_COUNT}
                        />
                    </div>
                </Tooltip>
            );
        }
        return (
            <NumberInput
                value={allocatedTime.value}
                onChange={handleValueChange}
                disabled={disabled}
                placeholder={undefined}
                min={0}
                max={MAX_NOTIFICATION_OFFSET_DAY_COUNT}
            />
        );
    };

    return (
        <div className={theme.inputRow}>
            <div className={theme.inputWithTextContainer}>
                <InputWithRightText
                    label={transTargetLabel}
                    rightText={`${unitLabel} ${toPerformLabel}`}
                >
                    {renderNumberInput()}
                </InputWithRightText>
            </div>
            {!sendImmediately && (
                <TimePickerInput
                    value={allocatedTime.sendTime.toDate()}
                    onChange={handleSendTimeChange}
                    label={pickerLabel}
                    format="HH:mm"
                    disabled={disabled}
                    nodeAfter={<Text>{tzAbbr}</Text>}
                />
            )}
        </div>
    );
}
